import React from 'react';
import { Calendar } from 'primereact/calendar';
import moment from 'moment';
import { DATE_FORMAT } from '../../../constants/constants';
import './styles.scss';

interface DatePickerV6Props {
    name: string;
    label?: string;
    value?: string; //DATE_FORMAT
    onChange?: (value: string, name: string) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    isRequired?: boolean;
    errorMessage?: string;
    className?: string;
    placeholder?: string;
    isDisabled?: boolean;
    isFocused?: boolean;
    minDate?: Date;
    maxDate?: Date;
    hintText?: string;
    hintTextWhiteColor?: boolean;
    dateFormat?: string;
    showIcon?: boolean;
    readOnlyInput?: boolean;
}

interface DatePickerV6State {
    formValidated: boolean;
}

class DatePickerV6 extends React.Component<DatePickerV6Props, DatePickerV6State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private calendarRef = React.createRef<Calendar>();
    private observer?: MutationObserver;

    constructor(props: DatePickerV6Props, context?: unknown) {
        super(props, context);
        this.state = {
            formValidated: false,
        };
    }

    componentDidMount() {
        this.attachFormValidationObserver();

        if (this.props.isFocused && this.calendarRef.current) {
            // PrimeReact Calendar focus
            setTimeout(() => {
                const input = this.calendarRef.current?.getInput();
                if (input) {
                    input.focus();
                }
            }, 100);
        }
    }

    componentWillUnmount() {
        this.removeFormValidationObserver();
    }

    /** Observe the form for class changes (e.g., Bootstrap `was-validated`) */
    private attachFormValidationObserver = () => {
        const form = this.containerRef.current?.closest('form');
        if (form) {
            this.observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    const target = mutation.target as HTMLElement;
                    if (target.classList.contains('was-validated')) {
                        this.setState({ formValidated: true });
                    }
                });
            });
            this.observer.observe(form, { attributes: true, attributeFilter: ['class'] });
        }
    };

    /** Remove observer when component unmounts */
    private removeFormValidationObserver = () => {
        if (this.observer) {
            this.observer.disconnect();
        }
    };

    private parseDate = (dateString: string): Date | null => {
        return dateString ? moment(dateString, DATE_FORMAT).toDate() : null;
    };

    private handleChange = (date: Date | null | undefined) => {
        if (this.props.onChange) {
            if (!date) {
                this.props.onChange('', this.props.name);
                return;
            }
            const utcDate = moment(date).utc(true);
            this.props.onChange(utcDate.format(DATE_FORMAT), this.props.name);
        }
    };

    private handleFocus = (e: React.FocusEvent) => {
        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    };

    private handleBlur = (e: React.FocusEvent) => {
        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
    };

    private handleKeyDown = (e: React.KeyboardEvent) => {
        if (this.props.onKeyDown) {
            this.props.onKeyDown(e);
        }
    };

    render() {
        const className = this.props.className || '';

        // Show error only after form is validated and if required field is empty
        const showError = this.state.formValidated && !!this.props.isRequired && (!this.props.value || this.props.value.trim().length === 0);

        const invalidClass = showError ? 'p-invalid' : '';
        const dateFormat = this.props.dateFormat || 'dd/mm/yy';

        return (
            <div className={`${className || 'mb-3'}`} ref={this.containerRef}>
                <div className='date-picker-wrapper'>
                    {this.props.label && (
                        <label htmlFor={this.props.name} className='date-picker-label'>
                            {this.props.label}
                        </label>
                    )}

                    <Calendar
                        ref={this.calendarRef}
                        id={this.props.name}
                        name={this.props.name}
                        value={this.parseDate(this.props.value || '')}
                        onChange={(e) => this.handleChange(e.value)}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        onKeyDown={this.handleKeyDown}
                        disabled={this.props.isDisabled}
                        placeholder={this.props.placeholder}
                        dateFormat={dateFormat}
                        showIcon={this.props.showIcon !== false} // default true
                        readOnlyInput={this.props.readOnlyInput}
                        minDate={this.props.minDate}
                        maxDate={this.props.maxDate}
                        className={`pacer-date-picker ${invalidClass}`}
                        inputClassName='form-control'
                        panelClassName='pacer-date-panel'
                        required={this.props.isRequired}
                    />

                    {showError && this.props.errorMessage && <div className='invalid-feedback'>{this.props.errorMessage}</div>}

                    {this.props.hintText && (
                        <div style={{ marginTop: '2px' }}>
                            <small className={`form-text sub-title ${this.props.hintTextWhiteColor ? 'text-light' : 'text-muted'}`}>
                                {this.props.hintText}
                            </small>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default DatePickerV6;
